/**
 * Background Removal Service using @huggingface/transformers and xrds/isnet-general-onnx-int8
 * Model repository: https://huggingface.co/xrds/isnet-general-onnx-int8
 * Licence: MIT (Commercially Permitted)
 * Base Architecture: IS-Net / DIS (Dichotomous Image Segmentation for general objects, machinery, vehicles, and humans)
 */

import {
  BG_REMOVAL_MODEL_ID,
  WorkerInferenceRequest,
  WorkerOutgoingMessage,
} from './bgRemoval.worker';

export interface BackgroundRemovalProgress {
  status: 'loading' | 'processing' | 'ready';
  message: string;
  progress?: number;
}

export type ProgressCallback = (progress: BackgroundRemovalProgress) => void;

interface TensorLike {
  dims?: number[];
  dispose?: () => void;
  mul: (val: number) => {
    to: (dtype: string) => unknown;
  };
  [index: number]: TensorLike;
}

interface InThreadPipeline {
  model: (inputs: { input: unknown }) => Promise<Record<string, TensorLike>>;
  processor: (image: unknown) => Promise<{ pixel_values: unknown }>;
  RawImage: {
    fromBlob: (source: Blob) => Promise<{
      width: number;
      height: number;
      resize: (width: number, height: number) => Promise<unknown>;
      putAlpha: (mask: unknown) => void;
      toBlob: (mimeType: string) => Promise<Blob>;
    }>;
    fromTensor: (tensor: unknown) => {
      resize: (width: number, height: number) => Promise<unknown>;
    };
  };
}

let inThreadPipelinePromise: Promise<InThreadPipeline> | null = null;
let activeWorker: Worker | null = null;

const createWorker = (): Worker | null => {
  if (typeof window === 'undefined' || typeof Worker === 'undefined') {
    return null;
  }
  try {
    return new Worker(new URL('./bgRemoval.worker.ts', import.meta.url), { type: 'module' });
  } catch (err) {
    console.warn('[BackgroundRemoval] Worker constructor unsupported:', err);
    return null;
  }
};

const getInThreadPipeline = async (onProgress?: ProgressCallback): Promise<InThreadPipeline> => {
  if (inThreadPipelinePromise) return inThreadPipelinePromise;

  inThreadPipelinePromise = (async () => {
    onProgress?.({
      status: 'loading',
      message: 'Loading background removal model...',
    });

    const { AutoModel, AutoProcessor, RawImage, env } = await import('@huggingface/transformers');

    env.allowLocalModels = false;
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = false;
      env.backends.onnx.wasm.numThreads = 1;
    }

    const progressCallback = (info: { status?: string; progress?: number }) => {
      if (!onProgress) return;
      if (info.status === 'progress' && typeof info.progress === 'number') {
        onProgress({
          status: 'loading',
          message: `Downloading model: ${Math.round(info.progress)}%`,
          progress: info.progress,
        });
      } else if (info.status === 'initiate' || info.status === 'download') {
        onProgress({
          status: 'loading',
          message: 'Downloading background removal model...',
        });
      }
    };

    const [model, processor] = await Promise.all([
      AutoModel.from_pretrained(BG_REMOVAL_MODEL_ID, {
        progress_callback: progressCallback,
      }),
      AutoProcessor.from_pretrained(BG_REMOVAL_MODEL_ID, {}),
    ]);

    onProgress?.({
      status: 'ready',
      message: 'Model loaded successfully',
    });

    return {
      model: model as InThreadPipeline['model'],
      processor: processor as InThreadPipeline['processor'],
      RawImage: RawImage as InThreadPipeline['RawImage'],
    };
  })().catch((err) => {
    inThreadPipelinePromise = null;
    throw err;
  });

  return inThreadPipelinePromise;
};

const removeBackgroundInThread = async (
  file: File,
  onProgress?: ProgressCallback
): Promise<File> => {
  const { model, processor, RawImage } = await getInThreadPipeline(onProgress);

  onProgress?.({
    status: 'processing',
    message: 'Segmenting foreground...',
  });

  const rawImage = await RawImage.fromBlob(file);
  const origW = rawImage.width;
  const origH = rawImage.height;

  let inputForModel = rawImage;
  if (origW > 1024 || origH > 1024) {
    inputForModel = (await rawImage.resize(1024, 1024)) as typeof rawImage;
  }

  // Pre-process the image
  const { pixel_values } = await processor(inputForModel);

  // Run inference to predict foreground matte
  const result = await model({ input: pixel_values });
  const outputTensor = result.output || result.output0 || result[Object.keys(result)[0]];
  const maskSlice = outputTensor.dims && outputTensor.dims.length === 4 ? outputTensor[0] : outputTensor;

  // Dispose unused intermediate tensors immediately
  for (const k of Object.keys(result)) {
    const t = result[k];
    if (t !== outputTensor && typeof t?.dispose === 'function') {
      try { t.dispose(); } catch { /* ignore */ }
    }
  }
  if (typeof (pixel_values as { dispose?: () => void })?.dispose === 'function') {
    try { (pixel_values as { dispose: () => void }).dispose(); } catch { /* ignore */ }
  }

  onProgress?.({
    status: 'processing',
    message: 'Encoding transparent PNG...',
  });

  // Resize alpha matte back to original dimensions
  const scaledTensor = maskSlice.mul(255).to('uint8');
  const mask = await RawImage
    .fromTensor(scaledTensor)
    .resize(origW, origH);

  if (typeof outputTensor?.dispose === 'function') {
    try { outputTensor.dispose(); } catch { /* ignore */ }
  }

  // Apply alpha mask directly in-place
  rawImage.putAlpha(mask);

  const resultBlob = await rawImage.toBlob('image/png');
  const baseName = file.name.replace(/\.[^/.]+$/, '');
  const transparentFileName = `${baseName}-transparent.png`;

  return new File([resultBlob], transparentFileName, { type: 'image/png' });
};

const removeBackgroundWithWorker = (
  worker: Worker,
  file: File,
  onProgress?: ProgressCallback
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    const messageHandler = (e: MessageEvent<WorkerOutgoingMessage>) => {
      const msg = e.data;
      if (!msg || msg.id !== requestId) return;

      if (msg.type === 'progress') {
        onProgress?.({
          status: msg.status,
          message: msg.message,
          progress: msg.progress,
        });
      } else if (msg.type === 'success') {
        cleanup();
        const processedFile = new File([msg.blob], msg.fileName, { type: 'image/png' });
        resolve(processedFile);
      } else if (msg.type === 'error') {
        cleanup();
        reject(new Error(msg.error));
      }
    };

    const errorHandler = (err: ErrorEvent) => {
      cleanup();
      // On fatal worker crash, reset active worker so a clean worker is created next time
      try {
        worker.terminate();
      } catch {
        // ignore
      }
      activeWorker = null;
      reject(new Error(err.message || 'Worker encountered an unexpected error.'));
    };

    const cleanup = () => {
      worker.removeEventListener('message', messageHandler);
      worker.removeEventListener('error', errorHandler);
    };

    worker.addEventListener('message', messageHandler);
    worker.addEventListener('error', errorHandler);

    const request: WorkerInferenceRequest = {
      id: requestId,
      action: 'remove-background',
      blob: file,
      fileName: file.name,
    };

    worker.postMessage(request);
  });
};

/**
 * Removes background from an image file using xrds/isnet-general-onnx-int8.
 * Executes inside Web Worker off the main UI thread.
 * Never silently runs heavy inference on the main thread if the worker is available.
 */
export const removeImageBackground = async (
  file: File,
  onProgress?: ProgressCallback
): Promise<File> => {
  if (typeof window !== 'undefined' && typeof Worker !== 'undefined') {
    if (!activeWorker) {
      activeWorker = createWorker();
    }

    if (activeWorker) {
      try {
        return await removeBackgroundWithWorker(activeWorker, file, onProgress);
      } catch (workerError: unknown) {
        const errorMsg = workerError instanceof Error
          ? workerError.message
          : 'Background removal worker failed to process image.';
        console.error('[BackgroundRemoval] Worker inference error:', errorMsg);
        // Do NOT silently fall back to the main UI thread - stop safely to preserve responsiveness
        throw new Error(errorMsg);
      }
    }
  }

  // Only reached in non-Worker environments (e.g. testing environments without Worker support)
  return await removeBackgroundInThread(file, onProgress);
};
